import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  verifyPasswordResetCodeWrapper,
  confirmPasswordResetWrapper,
} from "../firebase";
import AuthLayout from "../components/auth/AuthLayout";
import LoaderLoop from "../components/shared/loader/LoaderLoop";
import "./AuthPages.css";

function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [step, setStep] = useState("loading"); // loading | form | success | error
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode !== "resetPassword" || !oobCode) {
      setStep("error");
      setError("הקישור אינו תקין.");
      return;
    }

    firebaseVerifyCode();
  }, [mode, oobCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const firebaseVerifyCode = async () => {
    try {
      const associatedEmail = await verifyPasswordResetCodeWrapper(oobCode);
      setEmail(associatedEmail);
      setStep("form");
    } catch (err) {
      console.error("[CK-AUTH-RESET] Code verification failed:", err);
      if (err.code === "auth/expired-action-code") {
        setError("הקישור פג תוקף. אנא בקשו קישור חדש לאיפוס סיסמא.");
      } else if (err.code === "auth/invalid-action-code") {
        setError("הקישור כבר נוצל או שאינו תקין. אנא בקשו קישור חדש.");
      } else {
        setError("שגיאה באימות הקישור. אנא נסו שוב.");
      }
      setStep("error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!password) {
      setError("יש להזין סיסמא חדשה");
      return;
    }
    if (password.length < 6) {
      setError("הסיסמא חייבת להכיל לפחות 6 תווים");
      return;
    }
    if (password !== confirmPassword) {
      setError("הסיסמאות אינן תואמות");
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmPasswordResetWrapper(oobCode, password);
      setStep("success");
    } catch (err) {
      console.error("[CK-AUTH-RESET] Password reset failed:", err);
      if (err.code === "auth/expired-action-code") {
        setError("הקישור פג תוקף. אנא בקשו קישור חדש.");
      } else if (err.code === "auth/weak-password") {
        setError("הסיסמא חלשה מדי. נסו סיסמא חזקה יותר.");
      } else {
        setError("שגיאה באיפוס הסיסמא. נסו שוב.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout variant="login">
          {step === "loading" && (
            <>
              <div className="auth-head">
                <h1>איפוס סיסמא</h1>
                <p>מאמת את הקישור...</p>
              </div>
              <div className="auth-form email-callback-loading">
                <LoaderLoop size="md" />
              </div>
            </>
          )}

          {step === "form" && (
            <>
              <div className="auth-head">
                <h1>איפוס סיסמא</h1>
                <p>
                  הזינו סיסמא חדשה עבור{" "}
                  <strong dir="ltr">{email}</strong>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label>סיסמא חדשה</label>
                  <div className="password-field">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      placeholder="לפחות 6 תווים"
                      className="email-login-input password-login-input"
                      dir="ltr"
                      autoComplete="new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="password-visibility-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? "הסתר" : "הצג"}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>אימות סיסמא</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError("");
                    }}
                    placeholder="הזינו את הסיסמא שוב"
                    className="email-login-input"
                    dir="ltr"
                    autoComplete="new-password"
                  />
                </div>

                {error && <span className="error-message">{error}</span>}

                <button
                  type="submit"
                  className="auth-action-btn"
                  disabled={isSubmitting || !password || !confirmPassword}
                >
                  {isSubmitting ? "מאפס סיסמא..." : "אפס סיסמא"}
                </button>
              </form>
            </>
          )}

          {step === "success" && (
            <>
              <div className="auth-head">
                <h1>הסיסמא שונתה בהצלחה!</h1>
              </div>
              <div className="auth-form auth-action-success">
                <div className="auth-action-success-icon">✓</div>
                <p className="auth-action-success-text">
                  הסיסמא החדשה שלכם נשמרה. כעת תוכלו להתחבר למערכת באמצעות
                  האימייל והסיסמא החדשה.
                </p>
                <Link to="/login" className="auth-action-btn">
                  מעבר להתחברות
                </Link>
              </div>
            </>
          )}

          {step === "error" && (
            <>
              <div className="auth-head">
                <h1>שגיאה באיפוס סיסמא</h1>
              </div>
              <div className="auth-form auth-action-error">
                <div className="auth-action-error-icon">✕</div>
                <p className="auth-action-error-text">{error}</p>
                <Link to="/login" className="auth-action-btn-secondary">
                  חזרה למסך הכניסה
                </Link>
              </div>
            </>
          )}
    </AuthLayout>
  );
}

export default AuthActionPage;
